import { useEntityInstances } from '@/features/custom-entity-instance/custom-entity-instance.queries';
import { useCustomEntityInstancePageSettings } from '@/features/custom-entity/custom-entity.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { EntityInstancePageSettingsTableColumn } from '@/types/custom-entity.types';
import { Button } from '@/components/ui/button';
import { RoomDateColumn } from '@/components/datatable/columns/rooms/rooms-columns';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import CustomEntityInstanceSheet from '@/components/sheets/custom-entity-instance/custom-entity-instance-sheet';
import CustomEntityInstanceViewSheet from '@/components/sheets/custom-entity-instance/custom-entity-instance-view-sheet';

const customEntityInstanceRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view']).optional(),
  custom_entity_instance: z.number().optional(),
});

export const Route = createFileRoute('/_app/(custom-entities)/custom-entities/$slug/')({
  validateSearch: zodSearchValidator(customEntityInstanceRouteSchema),
  component: CustomEntityPage,
});

function CustomEntityPage() {
  const { slug } = useParams({
    from: '/_app/(custom-entities)/custom-entities/$slug/',
  });

  const { data, isLoading } = useCustomEntityInstancePageSettings(slug);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!data) {
    return <div>Error</div>;
  }

  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <CustomEntityInstanceTabHeader title={data.name} />
      <CustomEntityInstanceTable id={data.id} columns={data.columns} />
    </div>
  );
}

function CustomEntityInstanceTabHeader({ title }: { title: string }) {
  const navigate = useNavigate();

  const createRoom = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{title}</span>
        <span className='text-base font-light text-neutral-500'></span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createRoom} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {title}
        </Button>
      </div>
      <CustomEntityInstanceSheet />
      <CustomEntityInstanceViewSheet />
    </div>
  );
}

function CustomEntityInstanceTable({
  id,
  columns,
}: {
  id: number;
  columns: {
    [key: string]: EntityInstancePageSettingsTableColumn;
  };
}) {
  const { t } = useTranslation(['room', 'common']);
  const navigate = useNavigate();
  const { data = [], isLoading } = useEntityInstances(id);

  const updateRoom = (id: number) => {
    navigate({ to: '.', search: { mode: 'update', room: id } });
  };

  const columnsLabels = Object.fromEntries(
    Object.entries(columns).map(([key, column]) => [key, column.name])
  );

  const tableColumns = [
    'id',
    'name',
    ...(Object.keys(columns) as (keyof typeof CustomEntityInstanceTable)[]),
    'createdAt',
    'updatedAt',
  ] as (keyof typeof CustomEntityInstanceTable)[];

  return (
    <DataTableSimple
      data={data}
      columns={tableColumns}
      isLoading={isLoading}
      columnsLabels={{
        id: 'Serial No',
        name: toCapitalize(t('title', { ns: 'common' })),
        ...columnsLabels,
        createdAt: t('created_at', { ns: 'common' }),
        updatedAt: t('updated_at', { ns: 'common' }),
      }}
      customRender={{
        name: (row) => (
          <Link
            className='min-w-[50px]'
            to='.'
            search={{
              custom_entity_instance: row.getValue('id') as number,
              mode: 'view',
            }}
          >
            {row.getValue('name')}
          </Link>
        ),
        createdAt: (row) => <RoomDateColumn date={row.getValue('createdAt')} />,
        updatedAt: (row) => <RoomDateColumn date={row.getValue('updatedAt')} />,
      }}
      extraColumns={[
        {
          id: 'actions',
          size: 50,
          cell: ({ row }) => (
            <DataTableRowActions
              row={row}
              menuItems={[
                {
                  label: toCapitalize(t('edit', { ns: 'common' })),
                  onClick: () => updateRoom(row.getValue('id')),
                  isLoading: false,
                },
              ]}
            />
          ),
        },
      ]}
      className='flex h-full flex-col overflow-y-auto'
    />
  );
}
